'use client';

import { useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import posthog from 'posthog-js';
import { tw } from '@tw';
import type { QuizTutorDto } from '@peach/types/quizzes/quiz.dto.js';
import type { QuizAttemptResultDto } from '@peach/types/quizzes/quiz-attempt.dto.js';
import { getMyQuizzes, setQuizHidden, updateQuiz } from '@/app/actions/quizzes';
import { callAction } from '@/lib/call-action';
import { toUpdateBody } from '@/lib/quiz/create-body';
import { InlineAuth } from '@/components/auth/InlineAuth';
import { useQuizPlay } from '@/components/quiz/useQuizPlay';
import { formatDate } from '@/lib/utils/date';
import type { SanitizedQuiz } from '@/lib/quiz/sanitize';
import { QuizPostEditor } from './QuizPostEditor';
import s from './sub.module.css';

/** Locked answers are drawn as bars, never as text: the page is not sent them. */
const LOCKED_WIDTHS = ['62%', '38%', '74%', '50%', '66%'];

interface QuizPostProps {
  quiz: SanitizedQuiz;
  /** Off the feed: only its owner is sent it, to show it again. */
  hidden: boolean;
  /** The viewer's last finished attempt, shown instead of the first question. */
  played: QuizAttemptResultDto | null;
  tutor: { username: string; firstName: string; picture: string | null };
  isOwner: boolean;
  loggedIn: boolean;
  /** The cheapest plan, named on the button that unlocks the answers; null for the owner. */
  unlock: { price: string; onUnlock: () => void } | null;
}

/**
 * One quiz as a post in the feed of a page on subscriptions (design A): free to play,
 * the score after a sign-in, and the right answers for members only. Its owner can edit it here.
 */
export function QuizPost(props: QuizPostProps) {
  const { quiz, tutor } = props;
  const router = useRouter();
  const [editing, setEditing] = useState<QuizTutorDto | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Held until the refresh lands, so a second tap can't flip it back on the old state.
  const [hiding, startHiding] = useTransition();

  const toggleHidden = () =>
    startHiding(async () => {
      setLoadError(null);
      const result = await callAction(() => setQuizHidden(quiz.id, !props.hidden, tutor.username));
      if (!result.success) return setLoadError('That did not save. Try again.');
      posthog.capture('owner_quiz_hidden', {
        hidden: !props.hidden,
        creator_username: tutor.username,
        provider_username: tutor.username,
        tutor_username: tutor.username,
      });
      router.refresh();
    });

  // The feed is the public quiz, without its answers: the editor reads the owner's own copy.
  const edit = async () => {
    setLoadError(null);
    const mine = await callAction(() => getMyQuizzes());
    const found = mine.success ? mine.data.find((q) => q.id === quiz.id) : undefined;
    if (!found) return setLoadError('The quiz could not be opened. Try again.');
    setEditing(found);
  };

  if (editing) {
    return (
      <QuizPostEditor
        quiz={editing}
        source={editing}
        who={{ firstName: tutor.firstName, picture: tutor.picture }}
        byline={`Quiz, ${editing.questions.length} questions, posted ${formatDate(quiz.publishedAt, 'D MMM')}`}
        cancelLabel="Cancel"
        onCancel={() => setEditing(null)}
        onSave={async (next) => {
          const result = await callAction(() => updateQuiz(quiz.id, toUpdateBody(next)));
          if (!result.success) return result.error;
          posthog.capture('owner_quiz_edited', {
            creator_username: tutor.username,
            provider_username: tutor.username,
            tutor_username: tutor.username,
            source: 'subscription_feed',
          });
          setEditing(null);
          router.refresh();
          return null;
        }}
      />
    );
  }

  if (props.hidden) {
    return (
      <article className={tw(s.post, s.postHidden)}>
        <header className={s.postHead}>
          <p className={s.postWho}>
            Hidden from your feed
            <span>{quiz.title}</span>
          </p>
          <button
            type="button"
            className={tw(s.outline, s.press)}
            disabled={hiding}
            onClick={toggleHidden}
          >
            Show
          </button>
        </header>
        {loadError && <p className={s.postError}>{loadError}</p>}
      </article>
    );
  }

  return (
    <QuizPostPlay
      {...props}
      onEdit={props.isOwner ? () => void edit() : null}
      onHide={props.isOwner && !hiding ? toggleHidden : null}
      error={loadError}
    />
  );
}

function QuizPostPlay({
  quiz,
  played,
  tutor,
  isOwner,
  loggedIn,
  unlock,
  onEdit,
  onHide,
  error: loadError,
}: QuizPostProps & {
  onEdit: (() => void) | null;
  onHide: (() => void) | null;
  error: string | null;
}) {
  const {
    phase,
    questions,
    currentIdx,
    total,
    flash,
    error,
    result,
    marks,
    missed,
    answered,
    handleAnswer,
    onGateSuccess,
    replay,
    cardRef,
    lockedHeight,
  } = useQuizPlay({ quiz, isOwner, tutorUsername: tutor.username, loggedIn, played });
  const playing = phase === 'inline' || phase === 'playing';

  return (
    <article
      className={s.post}
      ref={cardRef}
      // From the last answer to the score, the post keeps the height it had while playing.
      style={lockedHeight && phase !== 'result' ? { minHeight: lockedHeight } : undefined}
    >
      <header className={s.postHead}>
        {tutor.picture ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={tutor.picture} alt="" className={s.postTile} />
        ) : (
          <span className={s.postTile} aria-hidden="true">
            {tutor.firstName.charAt(0)}
          </span>
        )}
        <p className={s.postWho}>
          {tutor.firstName}
          <span>Quiz, {total} questions</span>
        </p>
        <p className={s.postDate}>{formatDate(quiz.publishedAt, 'D MMM')}</p>
        {onHide && (
          <button type="button" className={tw(s.outline, s.press)} onClick={onHide}>
            Hide
          </button>
        )}
        {onEdit && (
          <button type="button" className={tw(s.outline, s.press)} onClick={onEdit}>
            Edit
          </button>
        )}
      </header>
      <h3 className={s.ptitle}>{quiz.title}</h3>

      {playing && questions.length > 0 && (
        <>
          <div className={s.segs} aria-hidden="true">
            {Array.from({ length: total }, (_, i) => (
              <span key={i} className={i <= currentIdx ? s.segOn : undefined} />
            ))}
          </div>
          {/* Every question in one cell, only the current one shown: the post is as tall as
              its tallest question from the first, so it never resizes under the finger. */}
          <div className={s.deck}>
            {questions.map((q, qi) => {
              const active = qi === currentIdx;
              return (
                <div key={q.id} aria-hidden={!active} className={tw(s.card, !active && s.cardOff)}>
                  <div className={s.band}>
                    <p className={s.bandCount}>
                      Question {qi + 1} of {total}
                    </p>
                    <p className={tw(s.bandQ, 'notranslate')} translate="no">
                      {q.text}
                    </p>
                  </div>
                  <div className={s.picks}>
                    {q.options.map((option, i) => {
                      const on = active && flash?.optionId === option.id;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          disabled={!active || !!flash}
                          tabIndex={active ? undefined : -1}
                          className={tw(s.pick, s.press, on && s.pickOn)}
                          onClick={() => void handleAnswer(option.id)}
                        >
                          <span className={s.pickKey} aria-hidden="true">
                            {String.fromCharCode(65 + i)}
                          </span>
                          <span translate="no" className="notranslate">
                            {option.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {phase === 'finishing' && (
        <>
          <Squares marks={[]} total={total} />
          <p className={s.postNote}>Adding up your score…</p>
        </>
      )}

      {phase === 'gate' && (
        <>
          <Squares marks={[]} total={total} />
          <p className={tw(s.ptitle)}>Your score is ready</p>
          <div className="sfv2 mt-3">
            <InlineAuth
              id={`quiz-${quiz.id}`}
              source="quiz_answers"
              tutorUsername={tutor.username}
              tone="card"
              prompt="Sign in to see how you did"
              cta="Show my score"
              onSuccess={onGateSuccess}
            />
          </div>
        </>
      )}

      {phase === 'result' && result && (
        <>
          <div className={s.score}>
            <b>
              {result.score}/{result.totalQuestions}
            </b>
            <span>
              {missed.length > 0
                ? `Go through the rest with ${tutor.firstName} on a call`
                : 'Clean run: you missed none'}
            </span>
          </div>
          <Squares marks={marks} total={result.totalQuestions} />
          {missed.map((m, i) => (
            <div key={m.number} className={s.missed}>
              <span className={s.missedNo}>{m.number}</span>
              <div className="min-w-0 flex-1">
                <p className={tw(s.missedQ, 'notranslate')} translate="no">
                  {m.text}
                </p>
                {m.answer ? (
                  <p className={tw(s.missedA, 'notranslate')} translate="no">
                    {m.answer}
                  </p>
                ) : (
                  <div className={s.locked} aria-label="The answer is for members">
                    <LockIcon />
                    <span style={{ width: LOCKED_WIDTHS[i % LOCKED_WIDTHS.length] }} />
                  </div>
                )}
              </div>
            </div>
          ))}
          {missed.length > 0 && !answered && unlock && (
            <button type="button" className={tw(s.unlock, s.press)} onClick={unlock.onUnlock}>
              See the {missed.length === 1 ? 'answer' : `${missed.length} answers`} you missed, from{' '}
              {unlock.price}
            </button>
          )}
          <button type="button" className={tw(s.outline, s.press, 'mt-3')} onClick={replay}>
            Replay
          </button>
        </>
      )}

      {(error || loadError) && <p className={s.postError}>{error ?? loadError}</p>}
    </article>
  );
}

/** The result in one row: tangerine for right, open for missed, grey while it is not known. */
function Squares({ marks, total }: { marks: boolean[]; total: number }) {
  const known = marks.length > 0;
  const right = marks.filter(Boolean).length;
  return (
    <div
      className={s.squares}
      role="img"
      aria-label={known ? `${right} of ${total} right` : `${total} questions answered`}
    >
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={!known ? s.sqWait : marks[i] ? s.sqHit : undefined} />
      ))}
    </div>
  );
}

const LockIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);
